import { useEffect, useRef } from 'react';
import { Loader } from '@googlemaps/js-api-loader';

export interface ParsedAddress {
  address1: string;
  city: string;
  state: string;
  zip: string;
}

const autocompleteEnabled = import.meta.env.VITE_ADDRESS_AUTOCOMPLETE_ENABLED === 'true';

const loader = new Loader({
  apiKey: import.meta.env.VITE_GOOGLE_PLACES_API_KEY ?? '',
});

let placesLibraryPromise: Promise<google.maps.PlacesLibrary> | null = null;

function getPlacesLibrary(): Promise<google.maps.PlacesLibrary> {
  if (!placesLibraryPromise) {
    placesLibraryPromise = loader.importLibrary('places');
  }
  return placesLibraryPromise;
}

export function useAddressAutocomplete(
  input: HTMLInputElement | null,
  onPlaceSelected: (address: ParsedAddress) => void,
  options: { countries?: string[]; disabled?: boolean } = {}
) {
  const { countries = ['us'], disabled = false } = options;
  const callbackRef = useRef(onPlaceSelected);
  callbackRef.current = onPlaceSelected;

  useEffect(() => {
    if (!autocompleteEnabled || disabled || !input) return;

    let removeListener: (() => void) | undefined;
    let cancelled = false;

    getPlacesLibrary().then((places) => {
      if (cancelled || !input.isConnected) return;

      const autocomplete = new places.Autocomplete(input, {
        types: ['address'],
        componentRestrictions: { country: countries },
        fields: ['address_components'],
      });

      const listener = autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (!place.address_components) return;

        const get = (type: string, nameType: 'long_name' | 'short_name' = 'long_name') =>
          place.address_components!.find((c) => c.types.includes(type))?.[nameType] ?? '';

        callbackRef.current({
          address1: [get('street_number'), get('route')].filter(Boolean).join(' '),
          city: get('locality') || get('sublocality_level_1') || get('postal_town'),
          state: get('administrative_area_level_1', 'short_name'),
          zip: get('postal_code'),
        });
      });

      removeListener = () => google.maps.event.removeListener(listener);
    });

    return () => {
      cancelled = true;
      removeListener?.();
    };
  }, [disabled, input]);
}
